import NavBarLandscape from "./NavBar/NavBarLandscape";
import { useEffect, Suspense, lazy } from "react";
import { Helmet } from "react-helmet-async";
import LoadingScreen from "./LoadingScreen";
import useImagesLoaded from "./useImagesLoaded";
import "../css/HomePage.css";
import { Link } from 'react-router-dom';

// Eagerly load above-the-fold components
import AboutUs from "./AboutUs";

// Lazy load below-the-fold components
const OurServicesLandscape = lazy(() => import("./OurServicesLandscape"));
const ReviewsCarousel = lazy(() => import("./ReviewsCarousel"));
const LatestBlogPost = lazy(() => import('./LatestBlogPost'));
const FooterLandscape = lazy(() => import("./FooterLandscape"));

// Placeholder components for lazy-loaded sections
const LoadingPlaceholder = () => <div className="loading-placeholder" aria-label="Loading content..."></div>;

const HomePageLandscape = () => {
  const images = [
    "/Images/hero-roof-image.jpg",
    "/Images/pitched-roof-square.jpg",
    "/Images/flat-roof-square.jpg",
    "/Images/repair-square.jpg", 
  ];

  useEffect(() => {
    window.scrollTo(0, 0);

    const backgroundContainer = document.createElement("div");
    backgroundContainer.classList.add("about-us-background");
    backgroundContainer.style.backgroundColor = "white";
    backgroundContainer.style.height = "110vh";
    backgroundContainer.style.width = "100%";
    backgroundContainer.style.paddingLeft = "8vw";
    backgroundContainer.style.position = "fixed";
    backgroundContainer.style.top = 0;
    backgroundContainer.style.left = 0;
    backgroundContainer.style.zIndex = -1;

    document.body.appendChild(backgroundContainer);

    return () => {
      document.body.removeChild(backgroundContainer);
    };
  }, []);

  useEffect(() => {
    // Check if there's a scroll target from blog navigation
    const scrollTarget = sessionStorage.getItem('scrollTarget');
    if (scrollTarget) {
      // Clear the scroll target
      sessionStorage.removeItem('scrollTarget');
      
      // Function to handle scrolling
      const scrollToTarget = () => {
        const section = document.getElementById(scrollTarget);
        if (section) {
          const offset = window.innerHeight * 0.206;
          window.scrollTo({
            top: section.offsetTop - offset,
            behavior: "smooth"
          });
        }
      };

      // Wait for images and content to load
      if (document.readyState === 'complete') {
        setTimeout(scrollToTarget, 500);
      } else {
        window.addEventListener('load', () => {
          setTimeout(scrollToTarget, 500);
        });
      }
    }
  }, []);

  const scrollToSection = (sectionId) => {
    console.log("Scrolling to section:", sectionId);
    const section = document.getElementById(sectionId);
    if (section) {
      const offset = window.innerHeight * 0.206;
      window.scrollTo({
        top: section.offsetTop - offset,
        behavior: "smooth",
      });
    }
  };

  const imagesLoaded = useImagesLoaded(images);

  if (!imagesLoaded) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://brightonroofingspecialists.co.uk/" />
      </Helmet>
      <NavBarLandscape scrollToSection={scrollToSection} />
      <div className="home-page-container">
        <h1 className="main-title">Brighton Roofing Specialists - Professional Roofing Services in Brighton</h1>
        <div className="landing-page-container">
          <picture>
            <source srcSet="/Images/hero-roof-image.webp" type="image/webp" />
            <img
              src="/Images/hero-roof-image.jpg"
              alt="Professional roofing services in Brighton - slate roof with blue sky"
              className="hero-image"
              loading="eager" // Explicitly load this image eagerly as it's above the fold
              width="1500" // Add explicit width
              height="1000" // Add explicit height
              fetchpriority="high" // Add fetchpriority attribute
            />
          </picture>
          <div className="hero-overlay">
            <h2 className="hero-title">Brighton Roofing Specialists</h2>
            <p className="hero-subtitle">Professional roofing services with over 10 years of experience in Brighton & Hove and throughout East Sussex</p>
            <Link to="/contact" className="hero-cta">Get a Free Quote</Link>
          </div>
        </div>

        <div className="service-area-banner" role="status" aria-live="polite">
          Now serving Worthing, Lancing and the rest of West Sussex
        </div>
        
        <div className="home-content-section">
          <div className="blog-section">
            <Suspense fallback={<LoadingPlaceholder />}>
              <LatestBlogPost />
            </Suspense>
          </div>
          
          <div className="services-section" id="services">
            <Suspense fallback={<LoadingPlaceholder />}>
              <OurServicesLandscape />
            </Suspense>
          </div>
          
          <div className="reviews-section" id="reviews">
            <Suspense fallback={<LoadingPlaceholder />}>
              <ReviewsCarousel />
            </Suspense>
          </div>

          <div className="about-section" id="about">
            <AboutUs scrollToSection={scrollToSection} />
          </div>
        </div>
        
        <Suspense fallback={<LoadingPlaceholder />}>
          <FooterLandscape scrollToSection={scrollToSection} />
        </Suspense>
      </div>
    </>
  );
};

export default HomePageLandscape;
